import { Column } from "@/components/common/Table";
import { MedicationFillHistory } from "./medicationFillHistory";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useRef, useState, useEffect } from "react";
import { Typography } from "@/components/ui/typography";

// Component to handle conditional tooltip
const TruncatedText = ({ value }: { value: string }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [value]);

  const content = (
    <div
      ref={textRef}
      className={`w-full truncate whitespace-nowrap overflow-hidden ${isTruncated ? "cursor-pointer" : ""
        }`}
    >
      {value}
    </div>
  );

  if (!isTruncated) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="z-[9999] px-3 py-2 rounded-md shadow-md text-xs max-w-[260px] bg-gray-700 text-grey-100"
        >
          <Typography variant="regular-12" className="break-words">
            {value}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const columns: Column<MedicationFillHistory>[] = [
  {
    key: "drug",
    label: "Drug",
    isSortable: false,
    cellClassName: "min-w-[200px] max-w-[200px]",
    headerClassName: "min-w-[200px] max-w-[200px]",
    render: (row) => {
      const value = row.drug || "-";
      return <TruncatedText value={value} />;
    },
  },
  {
    key: "fillDate",
    label: "Fill Date",
    isSortable: false,
    cellClassName: "min-w-[100px]",
    headerClassName: "min-w-[100px]",
    render: (row) => {
      return <TruncatedText value={row.fillDate || "-"} />;
    },
  },
  {
    key: "daysSupply",
    label: "Days Supply",
    isSortable: false,
    cellClassName: "min-w-[85px] !pr-0",
    headerClassName: "min-w-[85px] !pr-0",
    render: (row) => {
      const value =
        row.daysSupply !== null && row.daysSupply !== undefined
          ? String(row.daysSupply)
          : "-";
      return <TruncatedText value={value} />;
    },
  },
  {
    key: "prescriber",
    label: "Prescriber",
    isSortable: false,
    cellClassName: "min-w-[75px] !pr-1",
    headerClassName: "min-w-[75px] !pr-1",
    render: (row) => {
      return <TruncatedText value={row.prescriber || "-"} />;
    },
  },
  {
    key: "pharmacy",
    label: "Pharmacy",
    isSortable: false,
    cellClassName: "min-w-[120px]",
    headerClassName: "min-w-[120px]",
    render: (row) => {
      return <TruncatedText value={row.pharmacy || "-"} />;
    },
  },
  {
    key: "class",
    label: "Class",
    isSortable: false,
    cellClassName: "min-w-[150px] max-w-[150px] !pr-1",
    headerClassName: "min-w-[150px] max-w-[150px] !pr-1",
    render: (row) => {
      return <TruncatedText value={row.class || "-"} />;
    },
  },
];
